import React from 'react';
import { Button, Checkbox, Modal, Select, Typography } from 'antd';

export type SimpleExportFormat = 'Word' | 'ePub';

interface SimpleExportFormatModalProps {
  visible: boolean;
  format: SimpleExportFormat;
  allowEpub: boolean;
  loading?: boolean;
  includeImages?: boolean;
  onIncludeImagesChange?: (includeImages: boolean) => void;
  onChange: (format: SimpleExportFormat) => void;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

const SimpleExportFormatModal: React.FC<SimpleExportFormatModalProps> = ({
  visible,
  format,
  allowEpub,
  loading = false,
  includeImages = true,
  onIncludeImagesChange,
  onChange,
  onCancel,
  onConfirm,
}) => {
  const formatGuidance = format === 'Word'
    ? 'Creates a .docx file for Microsoft Word or Kindle Create.'
    : 'Creates an .epub file for e-readers or direct upload to an eBook store.';

  return (
    <Modal
      title="Create export"
      visible={visible}
      onCancel={onCancel}
      width={420}
      centered
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={loading}
          onClick={() => void onConfirm()}
        >
          Create {format} export
        </Button>,
      ]}
    >
      <Typography.Paragraph>
        Choose the file type you need. This uses the completed translation and does not translate the book again.
      </Typography.Paragraph>
      <label htmlFor="simple-export-format">File format</label>
      <Select
        id="simple-export-format"
        aria-label="Export file format"
        value={format}
        onChange={(value) => onChange(value as SimpleExportFormat)}
        style={{ width: '100%', marginTop: 6, marginBottom: 10 }}
        disabled={loading}
      >
        <Select.Option value="Word">Word (.docx)</Select.Option>
        {allowEpub && <Select.Option value="ePub">EPUB (.epub)</Select.Option>}
      </Select>
      <Typography.Text type="secondary">{formatGuidance}</Typography.Text>
      {format === 'Word' && onIncludeImagesChange && (
        <div style={{ marginTop: 16 }}>
          <Checkbox checked={includeImages} onChange={event => onIncludeImagesChange(event.target.checked)} disabled={loading}>
            Include images
          </Checkbox>
          <Typography.Paragraph type="secondary" style={{ marginTop: 6, marginBottom: 0 }}>
            Turn off to export without images. Saved chapters and images stay unchanged.
          </Typography.Paragraph>
        </div>
      )}
    </Modal>
  );
};

export default SimpleExportFormatModal;
